import { useEffect } from "react";
import Navbar from "../../components/feature/Navbar";
import Footer from "../../components/feature/Footer";
import SEOHead from "../../components/feature/SEOHead";
import { useTheme } from "../../contexts/ThemeContext";

const SITE_URL = import.meta.env.VITE_SITE_URL ?? "https://trackly.app";
const privacyJsonLd = {
  "@context": "https://schema.org",
  "@type": "WebPage",
  "name": "Privacy Policy | Trackly",
  "description": "Trackly's privacy policy. Learn what data we collect, how we use it, and how you stay in control of your financial tracking data.",
  "url": `${SITE_URL}/privacy-policy`,
  "isPartOf": { "@type": "WebSite", "name": "Trackly", "url": SITE_URL },
};

const SECTIONS = [
  {
    title: "Information We Collect",
    icon: "ri-database-2-line",
    content: [
      {
        heading: "Account Information",
        body: "When you create a Trackly account, we collect your email address, display name, and profile photo if provided. This information is used solely to identify you within the platform and personalise your experience.",
      },
      {
        heading: "Flip & Sales Data",
        body: "All flip entries, profit calculations, cost inputs, and sale records you create are stored securely and are private to your account. We do not share, sell, or analyse your financial data for advertising purposes.",
      },
      {
        heading: "Usage Analytics",
        body: "We collect anonymised usage metrics — such as page visits, feature interactions, and session duration — to understand how users engage with Trackly and to improve the product over time.",
      },
    ],
  },
  {
    title: "How We Use Your Data",
    icon: "ri-settings-4-line",
    content: [
      {
        heading: "Service Delivery",
        body: "Your data powers the core Trackly experience: tracking flips, calculating margins, generating reports, and displaying your performance dashboard.",
      },
      {
        heading: "Communication",
        body: "We may send you transactional emails (e.g. password resets, billing receipts) and, if opted in, product updates and tips. You can unsubscribe from marketing emails at any time.",
      },
      {
        heading: "Security & Fraud Prevention",
        body: "We use your login activity and device information to detect suspicious access, prevent unauthorised logins, and keep your account safe.",
      },
    ],
  },
  {
    title: "Data Sharing & Third Parties",
    icon: "ri-share-line",
    content: [
      {
        heading: "We Do Not Sell Your Data",
        body: "Trackly does not sell, rent, or trade your personal information to any third party for commercial or advertising purposes. Full stop.",
      },
      {
        heading: "Service Providers",
        body: "We work with trusted sub-processors — including Supabase (database & auth), Stripe (payments), and Vercel (hosting) — who process data on our behalf under strict data processing agreements.",
      },
      {
        heading: "Legal Requirements",
        body: "We may disclose information if required by law, court order, or to protect the rights, property, or safety of Trackly, our users, or the public.",
      },
    ],
  },
  {
    title: "Data Retention & Deletion",
    icon: "ri-delete-bin-6-line",
    content: [
      {
        heading: "Retention Policy",
        body: "We retain your account data for as long as your account is active. Anonymised analytics data may be retained for up to 24 months to identify long-term product trends.",
      },
      {
        heading: "Account Deletion",
        body: "You can request full deletion of your account and all associated data at any time by emailing privacy@trackly.app. We will process deletion within 14 business days.",
      },
    ],
  },
  {
    title: "Your Rights",
    icon: "ri-shield-user-line",
    content: [
      {
        heading: "Access & Portability",
        body: "You have the right to request a copy of all personal data we hold about you in a machine-readable format.",
      },
      {
        heading: "Correction & Erasure",
        body: "You can update your account details at any time from your dashboard settings. For erasure requests beyond in-app controls, contact us directly.",
      },
      {
        heading: "GDPR & CCPA",
        body: "If you are located in the EU or California, you have additional rights under GDPR and CCPA respectively, including the right to object to processing and the right to opt out of data sales (we don't do this, but the right applies).",
      },
    ],
  },
  {
    title: "Cookies",
    icon: "ri-global-line",
    content: [
      {
        heading: "Essential Cookies",
        body: "We use strictly necessary cookies to maintain your session, remember your theme preference, and keep you signed in. These cannot be disabled.",
      },
      {
        heading: "Analytics Cookies",
        body: "With your consent, we use anonymous analytics cookies to understand traffic patterns and improve the platform. You can opt out via your browser settings.",
      },
    ],
  },
];

export default function PrivacyPolicy() {
  const { isDark } = useTheme();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, []);

  return (
    <div className={`min-h-screen transition-colors duration-300 ${isDark ? "bg-[#050505]" : "bg-[#f8f8f8]"}`}>
      <SEOHead
        title="Privacy Policy | Trackly — Reseller Profit Tracker"
        description="Trackly's privacy policy. Learn what data we collect, how we use it, and how you stay in control of your flip and financial tracking data."
        keywords="trackly privacy policy, reseller data protection, flip tracker privacy"
        canonical={`${SITE_URL}/privacy-policy`}
        jsonLd={privacyJsonLd}
      />
      <Navbar />

      <main className="pt-32 pb-24 px-8 lg:px-16">
        <div className="max-w-3xl mx-auto">

          {/* Header */}
          <div className="mb-16">
            <span className={`inline-flex items-center gap-2 font-inter text-xs font-medium px-3 py-1.5 rounded-full mb-6 ${isDark ? "bg-white/5 text-[#888]" : "bg-black/6 text-[#666]"}`}>
              <i className="ri-shield-check-line text-xs" />
              Last updated March 20, 2026
            </span>
            <h1 className={`font-syne text-4xl font-bold mb-5 leading-tight ${isDark ? "text-white" : "text-[#111111]"}`}>
              Privacy Policy
            </h1>
            <p className={`font-inter text-base leading-relaxed ${isDark ? "text-[#666666]" : "text-[#555555]"}`}>
              At Trackly, we believe your financial data is private by default. This policy explains exactly what we collect, why we collect it, and how you stay in control.
            </p>
          </div>

          {/* Sections */}
          <div className="flex flex-col gap-10">
            {SECTIONS.map((section) => (
              <section
                key={section.title}
                className={`rounded-2xl border p-8 transition-colors duration-300 ${isDark ? "bg-white/[0.02] border-white/6" : "bg-white border-black/8"}`}
              >
                <div className="flex items-center gap-3 mb-7">
                  <div className={`w-9 h-9 flex items-center justify-center rounded-xl ${isDark ? "bg-white/6" : "bg-black/5"}`}>
                    <i className={`${section.icon} text-base ${isDark ? "text-[#aaaaaa]" : "text-[#555555]"}`} />
                  </div>
                  <h2 className={`font-syne text-lg font-semibold ${isDark ? "text-white" : "text-[#111111]"}`}>
                    {section.title}
                  </h2>
                </div>
                <div className="flex flex-col gap-6">
                  {section.content.map((item) => (
                    <div key={item.heading}>
                      <h3 className={`font-inter text-sm font-semibold mb-2 ${isDark ? "text-[#cccccc]" : "text-[#222222]"}`}>
                        {item.heading}
                      </h3>
                      <p className={`font-inter text-sm leading-relaxed ${isDark ? "text-[#666666]" : "text-[#555555]"}`}>
                        {item.body}
                      </p>
                    </div>
                  ))}
                </div>
              </section>
            ))}
          </div>

          {/* Contact */}
          <div className={`mt-10 rounded-2xl border p-8 ${isDark ? "bg-white/[0.02] border-white/6" : "bg-white border-black/8"}`}>
            <h2 className={`font-syne text-lg font-semibold mb-3 ${isDark ? "text-white" : "text-[#111111]"}`}>
              Questions or Requests?
            </h2>
            <p className={`font-inter text-sm leading-relaxed mb-5 ${isDark ? "text-[#666666]" : "text-[#555555]"}`}>
              If you have any questions about this Privacy Policy, or wish to exercise your data rights, contact our privacy team directly:
            </p>
            <a
              href="mailto://support@trackly.to"
              className={`inline-flex items-center gap-2 font-inter text-sm font-medium cursor-pointer transition-colors duration-200 ${isDark ? "text-white hover:text-[#cccccc]" : "text-[#111111] hover:text-[#444444]"}`}
            >
              <i className="ri-mail-line" />
              support@trackly.to
            </a>
          </div>

        </div>
      </main>

      <Footer />
    </div>
  );
}
